/**
 * Diaper Search page — pipeline session_samples + filters
 */

import { create } from 'zustand';
import { getAnalysisSessions, AnalysisSession } from '../lib/api-clip-monitor';
import type { TriState } from '../lib/sessionSampleFields';
import { BOOLEAN_SESSION_FIELDS, DEFAULT_SORT_FIELD } from '../lib/sessionSampleFields';

function emptyBooleanFilters(): Record<string, TriState> {
  const o: Record<string, TriState> = {};
  for (const { key } of BOOLEAN_SESSION_FIELDS) {
    o[String(key)] = 'any';
  }
  return o;
}

function emptyNumericRange(): Record<string, { min: string; max: string }> {
  return {};
}

interface SearchStore {
  sessions: AnalysisSession[];
  loading: boolean;
  error: string | null;
  resultsPath: string | null;
  analysisBaseDir: string | null;
  loadedBucket: string | null;

  modelFilter: number | 'all';
  siteFilter: string | 'all';
  textSearch: string;
  /** Substring match on criterion_name / criterion_signature */
  textCriterion: string;
  booleanFilters: Record<string, TriState>;
  /** key → min/max strings (only non-empty bounds apply) */
  numericRange: Record<string, { min: string; max: string }>;

  sortField: string;
  sortDesc: boolean;

  setModelFilter: (v: number | 'all') => void;
  setSiteFilter: (v: string | 'all') => void;
  setTextSearch: (v: string) => void;
  setTextCriterion: (v: string) => void;
  setBooleanFilter: (key: string, v: TriState) => void;
  setNumericMin: (key: string, v: string) => void;
  setNumericMax: (key: string, v: string) => void;
  setSortField: (v: string) => void;
  setSortDesc: (v: boolean) => void;

  fetchSessions: (bucket: string) => Promise<void>;
  resetFilters: () => void;
}

export const useSearchStore = create<SearchStore>((set, get) => ({
  sessions: [],
  loading: false,
  error: null,
  resultsPath: null,
  analysisBaseDir: null,
  loadedBucket: null,

  modelFilter: 'all',
  siteFilter: 'all',
  textSearch: '',
  textCriterion: '',
  booleanFilters: emptyBooleanFilters(),
  numericRange: emptyNumericRange(),

  sortField: DEFAULT_SORT_FIELD,
  sortDesc: true,

  setModelFilter: (v) => set({ modelFilter: v }),
  setSiteFilter: (v) => set({ siteFilter: v }),
  setTextSearch: (v) => set({ textSearch: v }),
  setTextCriterion: (v) => set({ textCriterion: v }),
  setBooleanFilter: (key, v) =>
    set((s) => ({
      booleanFilters: { ...s.booleanFilters, [key]: v },
    })),
  setNumericMin: (key, min) =>
    set((s) => ({
      numericRange: {
        ...s.numericRange,
        [key]: { ...(s.numericRange[key] || { min: '', max: '' }), min },
      },
    })),
  setNumericMax: (key, max) =>
    set((s) => ({
      numericRange: {
        ...s.numericRange,
        [key]: { ...(s.numericRange[key] || { min: '', max: '' }), max },
      },
    })),
  setSortField: (v) => set({ sortField: v }),
  setSortDesc: (v) => set({ sortDesc: v }),

  fetchSessions: async (bucket: string) => {
    if (!bucket) {
      set({
        sessions: [],
        error: 'Select a data bucket (period) in the clip monitor first.',
        resultsPath: null,
        analysisBaseDir: null,
        loadedBucket: null,
      });
      return;
    }
    set({ loading: true, error: null });
    const res = await getAnalysisSessions(bucket);
    if (!res.ok) {
      set({
        loading: false,
        sessions: [],
        error: res.message || res.error || 'Failed to load analysis sessions',
        resultsPath: res.resultsPath ?? null,
        analysisBaseDir: res.analysisBaseDir ?? null,
        loadedBucket: null,
      });
      return;
    }
    set({
      loading: false,
      sessions: res.sessions as AnalysisSession[],
      error: null,
      resultsPath: res.resultsPath ?? null,
      analysisBaseDir: res.analysisBaseDir ?? null,
      loadedBucket: bucket,
    });
  },

  resetFilters: () =>
    set({
      modelFilter: 'all',
      siteFilter: 'all',
      textSearch: '',
      textCriterion: '',
      booleanFilters: emptyBooleanFilters(),
      numericRange: emptyNumericRange(),
      sortField: DEFAULT_SORT_FIELD,
      sortDesc: true,
    }),
}));
